<script lang="ts">
  import { Button, Icon, icons } from '@factry/components'
  import Graph from '../components/Graph.svelte'
  import MinMax from '../components/MinMax.svelte'
  import MinMaxModal from '../components/MinMaxModal.svelte'
  import { MeasurementStatus } from '../types'
  import { roundNumber } from '../utils/datasets'
  import type { AssetEvent, Dataset } from '../types'

  export let datasets: Dataset[] = []
  export let event: AssetEvent | undefined

  const visibleGraphs: Record<string, boolean> = {}
  const visibleGraphsType: Record<string, boolean> = {}

  let open: string = ''

  // Gets the base id for properties related to a set of measurements based a property that ends on '_partition_key' and has the same value as the PartitionKey in the measurements.
  function getBasePropertyIdForDataset (dataset: Dataset): string {
    if (!event) return ''

    const measurementKey = Object.keys(event).find(key =>
      event[key] === dataset.label && key.endsWith('_partition_key')
    )
    return measurementKey ? measurementKey.slice(0, -14) : ''
  }

  function openModal (label: string): () => void {
    return (): void => {
      open = label
    }
  }

  function toggleGraph (label: string): () => void {
    return (): void => {
      visibleGraphs[label] = !visibleGraphs[label]
    }
  }

  function toggleGraphType (label: string): () => void {
    return (): void => {
      visibleGraphsType[label] = !visibleGraphsType[label]
    }
  }
</script>

<style lang="scss">
  @import '../utils/base';

  .quality-wrapper {
    background-color: $white;
    border-radius: $radius;
    border: 1px solid $border;
    padding: ($spacing * 0.5) $spacing;

    @media (max-width: 600px) {
      padding: ($spacing * 0.5);
    }
  }

  .grid {
    display: grid;
    gap: $spacing;
    grid-template-columns: auto 1fr auto;
    overflow: hidden;

    @media (max-width: 1000px) {
      grid-template-columns: 1fr auto;

      .left-side,
      .graph {
        grid-column-start: span 2;
      }
    }

    @media (max-width: 600px) {
      grid-template-columns: 1fr;

      .left-side,
      .graph {
        flex-wrap: wrap;
        grid-column-start: 1;
        max-width: 100% !important;
      }
    }
  }

  .left-side,
  .right-side {
    align-items: center;
    display: flex;
    gap: $spacing * 0.25;
  }

  .left-side {
    position: relative;

    &::after {
      background-color: $grey-lightest;
      block-size: 1px;
      content: '';
      inline-size: 100vw;
      inset-block-start: calc(calc($spacing * 0.5) * -1);
      inset-inline-start: 0;
      position: absolute;
      z-index: 1;
    }
  }

  .right-side {
    gap: $spacing;
    justify-content: end;
  }

  .label {
    font-weight: normal;
    margin-bottom: 0;
  }

  .show-modal {
    cursor: pointer;
  }

  .status {
    border-radius: $radius;
    border: 1px solid $border;
    font-size: $size-6;
    padding: 0 $spacing * 0.25;

    &::before {
      -webkit-font-smoothing: antialiased;
      font: var(--fa-font-solid);
      text-rendering: auto;
      margin-left: $spacing * 0.125;
      margin-right: $spacing * 0.25;
    }

    &.has-passed {
      background-color: $primary-light;
      border-color: $primary;
      color: $primary-dark;

      &::before {
        color: $primary-dark;
        content: '\f46c';
      }
    }

    &.has-failed {
      background-color: $danger-light;
      border-color: $danger;
      color: $danger-dark;

      &::before {
        color: $danger-dark;
        content: '\e4e3';
      }
    }
  }

  .graph {
    grid-column-start: span 3;
    height: 200px;
    margin-top: -$spacing;
    position: relative;
  }

  .panel-info {
    border-radius: $radius;

    > .icon {
      margin-right: 0.5ch;
    }
  }
</style>

<div class="quality-wrapper">
  <div class="grid">
    {#if event}
      {#each datasets as dataset}
        {@const basePropertyId = getBasePropertyIdForDataset(dataset)}
        {@const yValues = dataset.data.map(dp => dp.y)}
        {@const description = event[`${basePropertyId}_description`]}
        {@const max = Math.max(...yValues)}
        {@const min = Math.min(...yValues)}
        {@const mid = yValues.reduce((a, b) => a + b, 0) / yValues.length}
        {@const maxAcceptable = event[`${basePropertyId}_max`]}
        {@const minAcceptable = event[`${basePropertyId}_min`]}
        {@const status = yValues.some(v => v >= maxAcceptable || v <= minAcceptable) ? MeasurementStatus.Failed : MeasurementStatus.Passed}
        {@const UOM = event[`${basePropertyId}_uom`]}

        {#if open === dataset.label}
          <MinMaxModal
            {description}
            {max}
            {maxAcceptable}
            {mid}
            {min}
            {minAcceptable}
            {status}
            {UOM}
            label={dataset.label}
            on:close={() => { open = '' }}
          />
        {/if}

        <div class="left-side">
          <div class="label">
            {dataset.label}
          </div>
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div class="show-modal" on:click={openModal(dataset.label)}>
            <Icon icon={icons.info} />
          </div>
          <Button
            className={`is-small is-rounded ${visibleGraphs[dataset.label] ? 'is-link' : ''}`}
            icon={visibleGraphs[dataset.label] ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'}
            on:click={toggleGraph(dataset.label)}
            text={visibleGraphs[dataset.label] ? 'Hide graph' : 'Show graph'}
          />
          {#if visibleGraphs[dataset.label]}
            <Button
              className={`is-small is-rounded ${visibleGraphsType[dataset.label] ? 'is-link' : ''}`}
              icon={visibleGraphsType[dataset.label] ? 'fa-solid fa-chart-line' : 'fa-solid fa-chart-column'}
              on:click={toggleGraphType(dataset.label)}
              text={visibleGraphsType[dataset.label] ? 'Show line graph' : 'Show histogram'}
            />
          {/if}
        </div>

        <MinMax
          {max}
          {maxAcceptable}
          {mid}
          {min}
          {minAcceptable}
          {UOM}
        />

        <div class="right-side">
          <div class="value">
            {roundNumber(mid)}
            {UOM}
          </div>
          <div
            class="status"
            class:has-passed={status === MeasurementStatus.Passed}
            class:has-failed={status === MeasurementStatus.Failed}
          >
            <span>{`${status}`.toUpperCase()}</span>
          </div>
        </div>

        {#if visibleGraphs[dataset.label]}
          <section class="graph">
            <Graph
              {dataset}
              {event}
              {maxAcceptable}
              {minAcceptable}
              type={visibleGraphsType[dataset.label] ? 'bar' : 'line'}
            />
          </section>
        {/if}
      {:else}
        <div class="panel-block panel-info">
          <Icon icon={icons.info} />
          No measurements loaded. Please provide a uuid in the url e.g. /#/?uuid=12345678-1234-1234-1234-123456789ABC
        </div>
      {/each}
    {:else}
      <div class="panel-block panel-info">
        <Icon icon={icons.info} />
        No event loaded. Please provide a uuid in the url e.g. /#/?uuid=12345678-1234-1234-1234-123456789ABC
      </div>
    {/if}
  </div>
</div>
